import type { LeadFeedRow } from '../leadFeedMapper';
import type { Territory } from './types';

/** Client-only mirror of `BROKERMAX_ADMIN_CLERK_USER_IDS` for sidebar visibility. */
export function isBrokerMaxAdminUserId(
  userId: string | null | undefined
): boolean {
  const raw = import.meta.env.VITE_BROKERMAX_ADMIN_USER_IDS ?? '';
  const allowed = new Set(
    raw
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
  );
  return Boolean(userId && allowed.has(userId));
}

export interface AdminAccountSummaryRow {
  clerkUserId: string;
  name: string | null;
  email: string | null;
  balanceCached: number;
  buyerLead: number;
  rentalLead: number;
  stripeCustomerId: string | null;
  hasTerritory: boolean;
}

export interface AdminAccountDetail extends AdminAccountSummaryRow {
  territory: Territory | null;
}

export interface AdminLeadInventoryRow {
  leadId: string;
  mlsNumber: string | null;
  listingId: string | null;
  listingSlug: string | null;
  propertyAddress: string;
  propertyPrice: string;
  propertyType: string | null;
  leadType: 'buyer' | 'lease';
  ageDays: number;
  status: 'assigned' | 'unassigned';
  bucket: 'fresh' | 'clearance' | 'other';
  propertyFsa: string | null;
  visitorName: string;
  visitorEmail: string;
  visitorPhone: string;
  visitorIsAgent: string | null;
  visitorMessage: string;
  routingDisabled: boolean;
  submittedAt: string | null;
  /** Same anchor as My Leads “age” (submittedAt ?? createdAt ?? processedAt). */
  leadCreatedAtIso?: string | null;
  isFlagged: boolean;
  flagReason: string;
}

export interface AdminAccountsPageResponse {
  accounts: AdminAccountSummaryRow[];
  nextCursor: string | null;
}

export interface AdminLeadsPageResponse {
  leads: AdminLeadInventoryRow[];
  nextCursor: string | null;
  total: number;
}

export interface WalletResponse {
  balance: number;
  pricing: { buyerLead: number; rentalLead: number };
  capacityBuyer: number;
  capacityRental: number;
}

export interface InventorySummaryResponse {
  total: number;
  buyer: number;
  renter: number;
  clearance: number;
}

export interface AutoPurchaseSettings {
  buyer: boolean;
  renter: boolean;
  clearance: boolean;
}

export interface TerritoryResponse {
  territory: Territory | null;
}

export interface LeadsResponse {
  leads: LeadFeedRow[];
}

function resolveAccountApiUrl(path: string): string {
  const base = (
    import.meta.env.VITE_ACCOUNT_API_ORIGIN as string | undefined
  )?.replace(/\/$/, '');
  if (base) {
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }
  return path;
}

async function readApiError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* not JSON */
  }
  if (text.includes('Cannot GET') || res.status === 404) {
    return `API not found (${res.status}). Is the API running on port 8787 with the latest server code? Try: npm run dev:api`;
  }
  return (
    text.slice(0, 200) || res.statusText || `Request failed (${res.status})`
  );
}

async function apiFetch(
  path: string,
  getToken: () => Promise<string | null>,
  init: RequestInit = {}
): Promise<Response> {
  const token = await getToken();
  const headers = new Headers(init.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (
    init.body &&
    !(init.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(resolveAccountApiUrl(path), { ...init, headers });
}

export async function submitSupportRequest(
  getToken: () => Promise<string | null>,
  body: { message: string; name?: string; email?: string }
): Promise<void> {
  const res = await apiFetch('/api/account/support-request', getToken, {
    method: 'POST',
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
}

export async function fetchWallet(
  getToken: () => Promise<string | null>
): Promise<WalletResponse> {
  const res = await apiFetch('/api/account/wallet', getToken);
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(j.error ?? res.statusText);
  }
  return res.json() as Promise<WalletResponse>;
}

export async function fetchTerritory(
  getToken: () => Promise<string | null>
): Promise<TerritoryResponse> {
  const res = await apiFetch('/api/account/territory', getToken);
  if (!res.ok) throw new Error('Failed to load territory');
  return res.json() as Promise<TerritoryResponse>;
}

export async function saveTerritory(
  getToken: () => Promise<string | null>,
  body: {
    centerLat: number;
    centerLng: number;
    radiusM: number;
    postalFsaPrefixes?: string[];
  }
): Promise<TerritoryResponse> {
  const res = await apiFetch('/api/account/territory', getToken, {
    method: 'PUT',
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Failed to save territory');
  }
  return res.json() as Promise<TerritoryResponse>;
}

export async function generateTerritoryFsas(
  getToken: () => Promise<string | null>,
  body: {
    centerLat: number;
    centerLng: number;
    radiusM: number;
  }
): Promise<{ postalFsaPrefixes: string[] }> {
  const res = await apiFetch('/api/account/territory/fsas', getToken, {
    method: 'POST',
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Failed to generate FSAs');
  }
  return res.json() as Promise<{ postalFsaPrefixes: string[] }>;
}

export async function fetchAssignedLeads(
  getToken: () => Promise<string | null>
): Promise<LeadFeedRow[]> {
  const res = await apiFetch('/api/account/leads', getToken);
  if (!res.ok) throw new Error('Failed to load leads');
  const data = (await res.json()) as LeadsResponse;
  return data.leads;
}

export async function fetchInventorySummary(
  getToken: () => Promise<string | null>
): Promise<InventorySummaryResponse> {
  const res = await apiFetch('/api/account/inventory-summary', getToken);
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<InventorySummaryResponse>;
}

export async function fetchAutoPurchaseSettings(
  getToken: () => Promise<string | null>
): Promise<AutoPurchaseSettings> {
  const res = await apiFetch('/api/account/autopurchase', getToken);
  if (!res.ok) throw new Error(await readApiError(res));
  return res.json() as Promise<AutoPurchaseSettings>;
}

export async function saveAutoPurchaseSettings(
  getToken: () => Promise<string | null>,
  settings: AutoPurchaseSettings
): Promise<
  AutoPurchaseSettings & { assignmentsCreated: number; balance: number }
> {
  const res = await apiFetch('/api/account/autopurchase', getToken, {
    method: 'PUT',
    body: JSON.stringify(settings)
  });
  if (!res.ok) throw new Error(await readApiError(res));
  return res.json() as Promise<
    AutoPurchaseSettings & { assignmentsCreated: number; balance: number }
  >;
}

export async function patchAssignmentActive(
  getToken: () => Promise<string | null>,
  assignmentId: string,
  isActive: boolean
): Promise<void> {
  const res = await apiFetch(
    `/api/account/assignments/${encodeURIComponent(assignmentId)}`,
    getToken,
    {
      method: 'PATCH',
      body: JSON.stringify({ isActive })
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Failed to update lead');
  }
}

export interface BillingPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface BillingRecentPayment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
  description: string;
  receiptUrl: string | null;
}

export interface BillingPurchaseActivityItem {
  assignmentId: string;
  leadId: string;
  purchasedAt: string;
  source: string;
  leadType: 'buyer' | 'lease';
  amountCad: number;
  leadName: string | null;
  mlsNumber: string | null;
  listingId: string | null;
  address: string | null;
}

export async function postBillingCustomer(
  getToken: () => Promise<string | null>,
  body: { email?: string; name?: string }
): Promise<{ stripeCustomerId: string }> {
  const res = await apiFetch('/api/account/billing/customer', getToken, {
    method: 'POST',
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Could not create billing customer');
  }
  return res.json() as Promise<{ stripeCustomerId: string }>;
}

export async function fetchRecentPayments(
  getToken: () => Promise<string | null>
): Promise<{ payments: BillingRecentPayment[] }> {
  const res = await apiFetch('/api/account/billing/recent-payments', getToken);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Could not load payments');
  }
  return res.json() as Promise<{ payments: BillingRecentPayment[] }>;
}

export async function fetchPurchaseActivity(
  getToken: () => Promise<string | null>
): Promise<{ purchases: BillingPurchaseActivityItem[] }> {
  const res = await apiFetch(
    '/api/account/billing/purchase-activity',
    getToken
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Could not load purchase activity');
  }
  return res.json() as Promise<{ purchases: BillingPurchaseActivityItem[] }>;
}

export async function fetchPaymentMethods(
  getToken: () => Promise<string | null>
): Promise<{ paymentMethods: BillingPaymentMethod[] }> {
  const res = await apiFetch('/api/account/billing/payment-methods', getToken);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Could not load payment methods');
  }
  return res.json() as Promise<{ paymentMethods: BillingPaymentMethod[] }>;
}

export async function createSetupIntent(
  getToken: () => Promise<string | null>,
  body: { email?: string; name?: string }
): Promise<{ clientSecret: string }> {
  const res = await apiFetch('/api/account/billing/setup-intent', getToken, {
    method: 'POST',
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Could not start card setup');
  }
  return res.json() as Promise<{ clientSecret: string }>;
}

export async function createTopUpIntent(
  getToken: () => Promise<string | null>,
  amountCad: number,
  paymentMethodId: string
): Promise<{ clientSecret: string }> {
  const res = await apiFetch('/api/account/billing/top-up-intent', getToken, {
    method: 'POST',
    body: JSON.stringify({ amountCad, paymentMethodId })
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Could not start payment');
  }
  return res.json() as Promise<{ clientSecret: string }>;
}

/** Credits wallet from a succeeded PI (works without Stripe webhooks in dev). Idempotent. */
export async function syncPaymentIntentToWallet(
  getToken: () => Promise<string | null>,
  paymentIntentId: string
): Promise<{ ok: boolean; balance: number }> {
  const res = await apiFetch(
    '/api/account/billing/sync-payment-intent',
    getToken,
    {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId })
    }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Could not update wallet');
  }
  return res.json() as Promise<{ ok: boolean; balance: number }>;
}

export async function detachPaymentMethod(
  getToken: () => Promise<string | null>,
  paymentMethodId: string
): Promise<void> {
  const res = await apiFetch(
    `/api/account/billing/payment-methods/${encodeURIComponent(paymentMethodId)}`,
    getToken,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Could not remove card');
  }
}

export async function postDemoTopUp(
  getToken: () => Promise<string | null>,
  amount: number
): Promise<{
  ok: boolean;
  credited: number;
  assignmentsCreated: number;
  balance: number;
}> {
  const res = await apiFetch('/api/account/wallet/top-up', getToken, {
    method: 'POST',
    body: JSON.stringify({ amount })
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? 'Top-up failed');
  }
  return res.json() as Promise<{
    ok: boolean;
    credited: number;
    assignmentsCreated: number;
    balance: number;
  }>;
}

export async function adminFetchAccountsPage(
  getToken: () => Promise<string | null>,
  limit = 50,
  cursor?: string | null
): Promise<AdminAccountsPageResponse> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (cursor) qs.set('cursor', cursor);
  const res = await apiFetch(`/api/admin/el/accounts?${qs}`, getToken);
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<AdminAccountsPageResponse>;
}

export async function adminFetchAccountDetail(
  getToken: () => Promise<string | null>,
  clerkUserId: string
): Promise<AdminAccountDetail> {
  const res = await apiFetch(
    `/api/admin/el/accounts/${encodeURIComponent(clerkUserId)}`,
    getToken
  );
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<AdminAccountDetail>;
}

export async function adminPatchPricing(
  getToken: () => Promise<string | null>,
  clerkUserId: string,
  pricing: { buyerLead: number; rentalLead: number }
): Promise<{
  ok: boolean;
  pricing: { buyerLead: number; rentalLead: number };
}> {
  const res = await apiFetch(
    `/api/admin/el/accounts/${encodeURIComponent(clerkUserId)}/pricing`,
    getToken,
    { method: 'PATCH', body: JSON.stringify(pricing) }
  );
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<{
    ok: boolean;
    pricing: { buyerLead: number; rentalLead: number };
  }>;
}

export async function adminPostLedger(
  getToken: () => Promise<string | null>,
  clerkUserId: string,
  body: { direction: 'credit' | 'debit'; amount: number; memo: string }
): Promise<{ ok: boolean; balance: number }> {
  const res = await apiFetch(
    `/api/admin/el/accounts/${encodeURIComponent(clerkUserId)}/ledger`,
    getToken,
    { method: 'POST', body: JSON.stringify(body) }
  );
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<{ ok: boolean; balance: number }>;
}

export async function adminPutTerritory(
  getToken: () => Promise<string | null>,
  clerkUserId: string,
  body: {
    centerLat: number;
    centerLng: number;
    radiusM: number;
    postalFsaPrefixes?: string[];
  }
): Promise<{ territory: Territory }> {
  const res = await apiFetch(
    `/api/admin/el/accounts/${encodeURIComponent(clerkUserId)}/territory`,
    getToken,
    { method: 'PUT', body: JSON.stringify(body) }
  );
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<{ territory: Territory }>;
}

export async function adminFetchLeadsPage(
  getToken: () => Promise<string | null>,
  params: {
    status?: 'all' | 'assigned' | 'unassigned' | 'fresh' | 'clearance';
    missing?: 'none' | 'fsa' | 'name' | 'email' | 'phone';
    flagged?: 'all' | 'flagged';
    search?: string;
    cursor?: string | null;
    limit?: number;
  } = {}
): Promise<AdminLeadsPageResponse> {
  const qs = new URLSearchParams();
  qs.set('status', params.status ?? 'all');
  qs.set('missing', params.missing ?? 'none');
  qs.set('flagged', params.flagged ?? 'all');
  if (params.search?.trim()) qs.set('search', params.search.trim());
  qs.set('limit', String(params.limit ?? 100));
  if (params.cursor) qs.set('cursor', params.cursor);
  const res = await apiFetch(`/api/admin/el/leads?${qs.toString()}`, getToken);
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<AdminLeadsPageResponse>;
}

export async function adminPatchLead(
  getToken: () => Promise<string | null>,
  leadId: string,
  body: { propertyFsa?: string | null; routingDisabled?: boolean }
): Promise<{ ok: boolean }> {
  const res = await apiFetch(
    `/api/admin/el/leads/${encodeURIComponent(leadId)}`,
    getToken,
    { method: 'PATCH', body: JSON.stringify(body) }
  );
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<{ ok: boolean }>;
}

export async function adminAssignLead(
  getToken: () => Promise<string | null>,
  leadId: string,
  userIdOrQuery: string
): Promise<{ ok: boolean }> {
  const res = await apiFetch(
    `/api/admin/el/leads/${encodeURIComponent(leadId)}/assign`,
    getToken,
    { method: 'POST', body: JSON.stringify({ userId: userIdOrQuery }) }
  );
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<{ ok: boolean }>;
}

export async function adminUnassignLead(
  getToken: () => Promise<string | null>,
  leadId: string
): Promise<{ ok: boolean; removed: number }> {
  const res = await apiFetch(
    `/api/admin/el/leads/${encodeURIComponent(leadId)}/assign`,
    getToken,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    throw new Error(await readApiError(res));
  }
  return res.json() as Promise<{ ok: boolean; removed: number }>;
}

export interface ClearanceLeadRow {
  leadId: string;
  ageDays: number;
  mlsNumber: string | null;
  listingId: string | null;
  listingSlug: string | null;
  leadType: 'buyer' | 'lease';
  propertyType: string | null;
  inTerritory: boolean;
  priceCad: number;
  visitorName: string;
  visitorEmail: string;
  visitorPhone: string;
  hasPhone: boolean;
}

export async function fetchClearanceLeads(
  getToken: () => Promise<string | null>
): Promise<{ leads: ClearanceLeadRow[] }> {
  const res = await apiFetch('/api/account/clearance/leads', getToken);
  if (!res.ok) throw new Error(await readApiError(res));
  return res.json() as Promise<{ leads: ClearanceLeadRow[] }>;
}

export async function postClearancePurchase(
  getToken: () => Promise<string | null>,
  leadIds: string[]
): Promise<{ ok: boolean; created: number; debited: number; balance: number }> {
  const res = await apiFetch('/api/account/clearance/purchase', getToken, {
    method: 'POST',
    body: JSON.stringify({ leadIds })
  });
  if (!res.ok) throw new Error(await readApiError(res));
  return res.json() as Promise<{
    ok: boolean;
    created: number;
    debited: number;
    balance: number;
  }>;
}

export interface PremiumLeadRow {
  leadId: string;
  ageDays: number;
  mlsNumber: string | null;
  listingId: string | null;
  listingSlug: string | null;
  leadType: 'buyer' | 'lease';
  propertyType: string | null;
  inTerritory: boolean;
  priceCad: number;
  visitorName: string;
  visitorEmail: string;
  visitorPhone: string;
  hasPhone: boolean;
}

export async function fetchPremiumLeads(
  getToken: () => Promise<string | null>
): Promise<{ leads: PremiumLeadRow[] }> {
  const res = await apiFetch('/api/account/premium/leads', getToken);
  if (!res.ok) throw new Error(await readApiError(res));
  return res.json() as Promise<{ leads: PremiumLeadRow[] }>;
}

export async function postPremiumPurchase(
  getToken: () => Promise<string | null>,
  leadIds: string[]
): Promise<{ ok: boolean; created: number; debited: number; balance: number }> {
  const res = await apiFetch('/api/account/premium/purchase', getToken, {
    method: 'POST',
    body: JSON.stringify({ leadIds })
  });
  if (!res.ok) throw new Error(await readApiError(res));
  return res.json() as Promise<{
    ok: boolean;
    created: number;
    debited: number;
    balance: number;
  }>;
}
